import React, { lazy } from 'react';
import IconMemory from '@/assets/images/menu/icon-memory.react.svg?react';
import { AuthNodes } from '@/config/app/auth';
import { isFlutter, isMac, isWindows } from '@/infrastructure/product-info';
import { CapitalPlatform } from '@/infrastructure/utils/app';
import { ExperienceTag } from '@/presentation/components/experience-tag';
import {
  BIG_PIC_TIPS,
  FD_TIPS,
  JAVA_LEAK_TIPS,
  JAVA_TOP_TIPS,
  NATIVE_TIPS,
} from '@/presentation/components/issue-list/components/tips';
import { MenuIcon } from '@/presentation/components/menu-icon';
import { IRouteConfig } from '../types';

export const memoryRoutes = (): IRouteConfig[] => [
  {
    path: '/memory',
    name: '内存',
    hideInMenu: isFlutter(),
    disabled: isFlutter(),
    sort: 93,
    icon: <MenuIcon icon={IconMemory} fontSize={16} />,
    platforms: [
      CapitalPlatform.Android,
      CapitalPlatform.IOS,
      CapitalPlatform.Mac,
      CapitalPlatform.VisionOS,
      CapitalPlatform.HarmonyOS,
      CapitalPlatform.AppleTV,
      CapitalPlatform.Windows,
    ],
    disabledByFree: true,
    authName: AuthNodes.Memory,
    routes: [
      {
        platforms: [
          CapitalPlatform.Android,
          CapitalPlatform.IOS,
          CapitalPlatform.Mac,
          CapitalPlatform.VisionOS,
          CapitalPlatform.HarmonyOS,
          CapitalPlatform.AppleTV,
        ],
        path: '/memory/analytics',
        name: '指标分析',
        component: lazy(() => import('@/presentation/pages/memory/analytics')),
      },
      //  ios
      {
        path: '/memory/vc-leak/list',
        name: 'VC 内存泄漏',
        suffix: <ExperienceTag/>,
        component: lazy(() => import('@/presentation/pages/memory/vc-leak/list')),
        platforms: [
          CapitalPlatform.IOS,
          CapitalPlatform.VisionOS,
          CapitalPlatform.AppleTV,
        ],
      },
      {
        path: '/memory/vc-leak/detail',
        name: 'VC 内存泄漏详情',
        component: lazy(() => import('@/presentation/pages/memory/vc-leak/detail')),
        platforms: [
          CapitalPlatform.IOS,
          CapitalPlatform.VisionOS,
          CapitalPlatform.AppleTV,
        ],
        hideInMenu: true,
        whitelisted: true,
      },
      {
        path: '/memory/memory-detail/list',
        name: isMac() ? '内存图' : '内存详情',
        suffix: <ExperienceTag/>,
        component: lazy(() => import('@/presentation/pages/memory/memory-detail/list')),
        platforms: [
          CapitalPlatform.IOS,
          CapitalPlatform.Mac,
          CapitalPlatform.VisionOS,
          CapitalPlatform.AppleTV,
        ],
      },
      {
        path: '/memory/memory-detail/detail',
        name: isMac() ? '内存图个例' : '个例详情',
        component: lazy(() => import('@/presentation/pages/memory/memory-detail/detail')),
        hideInMenu: true,
        whitelisted: true,
        platforms: [
          CapitalPlatform.IOS,
          CapitalPlatform.Mac,
          CapitalPlatform.VisionOS,
          CapitalPlatform.AppleTV,
        ],
      },
      {
        path: '/memory/mem-graph/list',
        name: '内存详情个例',
        component: lazy(() => import('@/presentation/pages/memory/mem-graph/list')),
        hideInMenu: true,
        platforms: [
          CapitalPlatform.IOS,
          CapitalPlatform.Mac,
          CapitalPlatform.VisionOS,
          CapitalPlatform.AppleTV,
        ],
      },

      {
        path: '/memory/allocate/list',
        name: '内存分配堆栈',
        hideInMenu: !isMac() && !isWindows(),
        platforms: [
          CapitalPlatform.Mac,
          CapitalPlatform.Windows,
        ],
        suffix: <ExperienceTag/>,
        component: lazy(() => import('@/presentation/pages/memory/native/list')),
      },
      {
        path: '/memory/allocate/detail',
        name: '内存分配堆栈详情',
        component: lazy(() => import('@/presentation/pages/memory/native/detail')),
        hideInMenu: true,
        whitelisted: true,
        platforms: [
          CapitalPlatform.Mac,
          CapitalPlatform.Windows,
        ],
      },
      {
        path: '/memory/bad-malloc/list',
        name: '大块内存分配',
        suffix: <ExperienceTag/>,
        component: lazy(() => import('@/presentation/pages/memory/bad-malloc/list')),
        platforms: [
          CapitalPlatform.IOS,
          CapitalPlatform.Mac,
          CapitalPlatform.VisionOS,
          CapitalPlatform.AppleTV,
        ],
      },
      {
        path: '/memory/bad-malloc/detail',
        name: '大块内存分配详情',
        component: lazy(() => import('@/presentation/pages/memory/bad-malloc/detail')),
        hideInMenu: true,
        whitelisted: true,
        platforms: [
          CapitalPlatform.IOS,
          CapitalPlatform.Mac,
          CapitalPlatform.VisionOS,
          CapitalPlatform.AppleTV,
        ],
      },

      // android
      {
        path: '/memory/java-leak/list',
        name: 'Java 内存泄漏',
        component: lazy(() => import('@/presentation/pages/memory/java-leak/list')),
        suffix: <ExperienceTag/>,
        platforms: [
          CapitalPlatform.Android,
        ],
        breadcrumbSuffix: <span className={'text-[#7B889C]'}>{JAVA_LEAK_TIPS}</span>,
      },
      {
        path: '/memory/java-leak/detail',
        name: 'Java 内存泄漏详情',
        component: lazy(() => import('@/presentation/pages/memory/java-leak/detail')),
        hideInMenu: true,
        whitelisted: true,
        platforms: [
          CapitalPlatform.Android,
        ],
      },
      {
        path: '/memory/memory-detail/list',
        name: 'Java 内存详情',
        component: lazy(() => import('@/presentation/pages/memory/memory-detail/list')),
        platforms: [
          CapitalPlatform.Android,
        ],
        suffix: <ExperienceTag/>,
        breadcrumbSuffix: <span className={'text-[#7B889C]'}>{JAVA_TOP_TIPS}</span>,
      },
      {
        path: '/memory/memory-detail/detail',
        name: '个例详情',
        component: lazy(() => import('@/presentation/pages/memory/memory-detail/detail')),
        hideInMenu: true,
        platforms: [
          CapitalPlatform.Android,
        ],
        whitelisted: true,
      },

      {
        path: '/memory/fd/list',
        name: 'FD 详情',
        component: lazy(() => import('@/presentation/pages/memory/fd/list')),
        platforms: [
          CapitalPlatform.Android,
        ],
        suffix: <ExperienceTag/>,
        breadcrumbSuffix: <span className={'text-[#7B889C]'}>{FD_TIPS}</span>,
      },
      {
        path: '/memory/fd/detail',
        name: 'FD详情',
        component: lazy(() => import('@/presentation/pages/memory/fd/detail')),
        hideInMenu: true,
        whitelisted: true,
        platforms: [
          CapitalPlatform.Android,
        ],
      },
      {
        path: '/memory/native/list',
        name: 'Native 内存详情',
        component: lazy(() => import('@/presentation/pages/memory/native/list')),
        suffix: <ExperienceTag/>,
        breadcrumbSuffix: <span className={'text-[#7B889C]'}>{NATIVE_TIPS}</span>,
        platforms: [
          CapitalPlatform.Android,
        ],
      },
      {
        path: '/memory/native/detail',
        name: 'Native内存详情',
        component: lazy(() => import('@/presentation/pages/memory/native/detail')),
        hideInMenu: true,
        whitelisted: true,
        platforms: [
          CapitalPlatform.Android,
        ],
      },
      {
        path: '/memory/big-pic/list',
        name: '大图分析',
        component: lazy(() => import('@/presentation/pages/memory/big-pic/list')),
        suffix: <ExperienceTag/>,
        breadcrumbSuffix: <span className={'text-[#7B889C]'}>{BIG_PIC_TIPS}</span>,
        platforms: [
          CapitalPlatform.Android,
        ],
      },
      {
        path: '/memory/big-pic/detail',
        name: '大图分析详情',
        component: lazy(() => import('@/presentation/pages/memory/big-pic/detail')),
        hideInMenu: true,
        whitelisted: true,
        platforms: [
          CapitalPlatform.Android,
        ],
      },
      {
        path: '/memory/high/issues/list',
        name: '内存触顶',
        component: lazy(() => import('@/presentation/pages/memory/high/list')),
        platforms: [
          CapitalPlatform.IOS,
        ],
        suffix: <ExperienceTag/>,
      },
      {
        path: '/memory/high/issues/detail',
        name: '内存触顶详情',
        component: lazy(() => import('@/presentation/pages/memory/high/detail')),
        hideInMenu: true,
        platforms: [
          CapitalPlatform.IOS,
        ],
      },
    ],
  },
];

export default memoryRoutes;
